import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, PRODUCTS } from "../../data/content";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", phone: "", product: "", message: "" };

export const QuoteContact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!form.name.trim() || form.message.trim().length < 5) {
      toast.error("Please fill in your name and a brief message (min 5 characters).");
      return;
    }
    if (!emailOk) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Request received. Our team will respond within one business day.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? "Please check the highlighted fields and try again."
        : "Something went wrong. Please try again or call us directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-border px-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors font-mono-ibm";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-background py-24 md:py-32 border-t border-border scroll-mt-16"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Left */}
        <div>
          <p className="font-mono-ibm text-xs tracking-widest uppercase text-primary mb-6">
            / Get In Touch
          </p>
          <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-6xl md:text-8xl lg:text-9xl mb-12">
            Request
            <br />A <span className="text-primary">Quote</span>
          </h2>

          <div className="space-y-6 border-t border-border pt-8">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="contact-phone"
              className="group flex items-center gap-4 hover:text-primary transition-colors"
            >
              <span className="w-10 h-10 border border-border flex items-center justify-center group-hover:border-primary transition-colors">
                <Phone size={16} />
              </span>
              <span className="font-mono-ibm text-sm tracking-wide">{CONTACT.phone}</span>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email"
              className="group flex items-center gap-4 hover:text-primary transition-colors"
            >
              <span className="w-10 h-10 border border-border flex items-center justify-center group-hover:border-primary transition-colors">
                <Mail size={16} />
              </span>
              <span className="font-mono-ibm text-sm tracking-wide">{CONTACT.email}</span>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <span className="w-10 h-10 border border-border flex items-center justify-center shrink-0">
                <MapPin size={16} />
              </span>
              <div className="font-mono-ibm text-sm tracking-wide text-muted-foreground leading-relaxed">
                <p className="text-foreground">{CONTACT.company}</p>
                {CONTACT.addressLines.map((l) => (
                  <p key={l}>{l}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right - form */}
        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="border border-border p-6 md:p-10 bg-card"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono-ibm text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                Name *
              </label>
              <input
                data-testid="form-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Full name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono-ibm text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                Email *
              </label>
              <input
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono-ibm text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                Company
              </label>
              <input
                data-testid="form-company"
                value={form.company}
                onChange={update("company")}
                placeholder="Organisation"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono-ibm text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                Phone
              </label>
              <input
                data-testid="form-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="font-mono-ibm text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
              Product Interest
            </label>
            <select
              data-testid="form-product"
              value={form.product}
              onChange={update("product")}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              <option value="">Select a product…</option>
              {PRODUCTS.map((p) => (
                <option key={p.id} value={p.title}>
                  {p.title}
                </option>
              ))}
              <option value="Multiple / Custom">Multiple / Custom</option>
            </select>
          </div>

          <div className="mt-4">
            <label className="font-mono-ibm text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
              Message *
            </label>
            <textarea
              data-testid="form-message"
              value={form.message}
              onChange={update("message")}
              rows={5}
              placeholder="Tell us about your cargo, dimensions, quantity & destination…"
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="form-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full flex items-center justify-center gap-3 font-mono-ibm text-xs tracking-widest uppercase bg-primary text-primary-foreground px-6 py-4 hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending
              </>
            ) : (
              "Submit Request"
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
